/**
 * QuickBooks Sales Receipts Management Page
 *
 * Main page for QuickBooks integration.
 * Allows Owner/Manager/Admin to connect QuickBooks and upload sales receipts.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import Header from '../../components/layout/Header';
import { useOwnerManagerAdminAccess } from '../../hooks/useToolAccess';
import { useSessionAuth } from '../../hooks/useSessionAuth';
import StudioSelectorControl from '../../components/common/StudioSelectorControl';
import FeatureGate from '../../components/common/FeatureGate';
import ConnectionStatus from './components/ConnectionStatus';
import FileUpload from './components/FileUpload';
import UploadHistory from './components/UploadHistory';
import type { UploadResults } from '../../types/quickbooks';
import NoToolAccess from '../../components/common/NoToolAccess';
import { YouTubeVideoButton } from '../../components/common';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const QuickBooksManagement: React.FC = () => {
  const navigate = useNavigate();

  // Get authentication state
  const { isAuthenticated, isLoading: authLoading } = useSessionAuth();

  // Get studio access information (Owner/Manager/Admin)
  const {
    eligibleStudios,
    hasToolAccess,
    hasSingleStudio,
    isLoading: toolAccessLoading,
  } = useOwnerManagerAdminAccess();

  // Component state
  const [selectedStudioId, setSelectedStudioId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [uploadResults, setUploadResults] = useState<UploadResults | null>(
    null
  );

  // Handle studio selection and access control
  useEffect(() => {
    // Wait for authentication and tool access data to load
    if (authLoading || toolAccessLoading) {
      return;
    }

    // If user is not authenticated, redirect to login
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Auto-select if user has exactly one eligible studio
    if (hasSingleStudio) {
      const [studio] = eligibleStudios;
      setSelectedStudioId(studio.id);
    }
  }, [
    navigate,
    authLoading,
    isAuthenticated,
    toolAccessLoading,
    hasSingleStudio,
    eligibleStudios,
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleUploadComplete = (results: UploadResults) => {
    setUploadResults(results);
    // Switch to history tab to show results
    setActiveTab(1);
  };

  // Note: OAuth success is now handled via localStorage in ConnectionStatus component
  // (window.opener is null after OAuth redirect, so we use localStorage instead of postMessage)

  // Show no access message if user doesn't have required permissions
  if (!authLoading && !toolAccessLoading && !hasToolAccess) {
    return (
      <NoToolAccess
        toolName="QuickBooks Sales Receipts"
        requiredRoles={['owner', 'manager', 'admin']}
      />
    );
  }

  // Show loading while checking access
  if (authLoading || toolAccessLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Page Header */}
        <Box mb={4}>
          <Typography variant="h4" component="h1" gutterBottom>
            QuickBooks Sales Receipts
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Upload sales receipts from Dance Studio Pro to QuickBooks Online
          </Typography>
        </Box>

        {/* Tutorial Video */}
        <Box
          sx={{
            mb: 4,
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            Watch our tutorial to learn how to upload sales receipts to
            QuickBooks Online.
          </Typography>
          <YouTubeVideoButton
            videoId="TQnYCa1qWIk"
            videoTitle="How to Upload QuickBooks Sales Receipts on tools.dance"
            buttonText="Watch Tutorial"
            variant="outlined"
            size="small"
          />
        </Box>

        {/* Studio Selector - Only shows for multiple studios */}
        <Box mb={4}>
          <StudioSelectorControl
            studios={eligibleStudios}
            selectedStudioId={selectedStudioId}
            onStudioChange={setSelectedStudioId}
          />
        </Box>

        {/* Info Alert for Multiple Studios */}
        {eligibleStudios.length > 1 && !selectedStudioId && (
          <Box mb={4}>
            <Alert severity="info" sx={{ maxWidth: 400 }}>
              Please select a studio to manage QuickBooks integration.
            </Alert>
          </Box>
        )}

        {/* Content area - only show if studio is selected */}
        {selectedStudioId && (
          <FeatureGate
            requirement={{ minimumPlan: 'growth' }}
            featureName="QuickBooks Sales Receipts"
            upgradeDescription="Upload sales receipts from Dance Studio Pro directly to QuickBooks Online. Automate your bookkeeping workflow and save hours of manual data entry."
          >
            {/* Connection Status Panel */}
            <Box mb={4}>
              <ConnectionStatus studioId={selectedStudioId} />
            </Box>

            {/* Tabs for Upload and History */}
            <Paper sx={{ p: 3 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="QuickBooks tabs"
              >
                <Tab label="Upload Receipts" />
                <Tab label="Upload History" />
              </Tabs>

              <TabPanel value={activeTab} index={0}>
                <FileUpload
                  studioId={selectedStudioId}
                  onParsedData={() => {}}
                  onUploadComplete={handleUploadComplete}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <UploadHistory
                  studioId={selectedStudioId}
                  latestResults={uploadResults}
                />
              </TabPanel>
            </Paper>
          </FeatureGate>
        )}
      </Container>
    </>
  );
};

export default QuickBooksManagement;
